import {
  dismissAllNotices,
  muteErrorNotices,
  unmuteErrorNotices,
} from "src/domains/shared/utils/notice";
import {
  enterLoginSupersededFetchMode,
  exitLoginSupersededFetchMode,
} from "./exclusiveLoginFetchGate";
import { LOGIN_ROUTE, SIGNED_OUT_ROUTE } from "./loginRoutes";
import { isPartnerSsoSession } from "./loginSessionSource";
import { readLoginTabSignal, writeLoginTabSignal } from "./loginTabSignal";
import { signOutThisBrowser } from "./signOutThisBrowser";

/**
 * 밀려남 단일 처리기 (design D7).
 *
 * 감지 경로(fetch 관문·열린 화면 감시자·다른 탭 신호)와 무관하게 이 함수 하나로 모인다. 한 번에 하나만
 * 진행하고, 진행 중 재호출은 같은 진행을 돌려준다. 끝난 뒤에는 다시 진행할 수 있다(같은 페이지에서 다시 로그인한
 * 세션이 또 밀려나는 경우). 재시도·토큰 갱신·해제 호출은 하지 않는다 — 밀려난 로그인은 서버에서 이미 끝났다.
 */

/** 비밀번호 로그인 세션의 밀려남 사유 — 로그인 화면이 비밀번호 재로그인 안내를 보인다. */
export const LOGIN_REASON_SUPERSEDED = "superseded";

/** 파트너 SSO 세션의 밀려남 사유 — 로그인 화면이 파트너 포털(하이보드)에서 다시 접속하라는 안내를 보인다. */
export const LOGIN_REASON_SUPERSEDED_PARTNER = "superseded-partner";

export const SUPERSEDED_LOGIN_ROUTE = `${LOGIN_ROUTE}?reason=${LOGIN_REASON_SUPERSEDED}` as const;

export const SUPERSEDED_PARTNER_LOGIN_ROUTE =
  `${LOGIN_ROUTE}?reason=${LOGIN_REASON_SUPERSEDED_PARTNER}` as const;

/**
 * 처리기가 쓰는 앱 의존. 처리기는 라우터·쿼리 클라이언트를 직접 import 하지 않고, `_app` 의
 * `useBindLoginSupersededHandler` 가 마운트 때 주입한다.
 */
export type LoginSupersededHandlerDeps = {
  /** 소프트 내비게이션. 하드 내비게이션은 미저장 변경의 `beforeunload` 확인에 막힌다. */
  replaceRoute: (url: string) => Promise<unknown>;
  clearQueryCache: () => void;
};

let boundDeps: LoginSupersededHandlerDeps | null = null;
const depsWaiters = new Set<(deps: LoginSupersededHandlerDeps) => void>();
let inProgress: Promise<void> | null = null;

/**
 * 처리기에 앱 의존을 주입한다. 주입 전에 시작된 처리는 여기서 이어진다. 반환 함수로 주입을 거둔다
 * (그 사이 다른 주입이 들어왔으면 그것은 남긴다).
 */
export function bindLoginSupersededHandler(deps: LoginSupersededHandlerDeps): () => void {
  boundDeps = deps;
  const waiters = [...depsWaiters];
  depsWaiters.clear();
  waiters.forEach((resume) => resume(deps));

  return () => {
    if (boundDeps === deps) boundDeps = null;
  };
}

function waitForDeps(): Promise<LoginSupersededHandlerDeps> {
  if (boundDeps) return Promise.resolve(boundDeps);
  return new Promise((resume) => depsWaiters.add(resume));
}

/** 밀려남 처리의 종착지 — 로그아웃이 먼저 전파됐으면 홈, 아니면 세션 출처에 맞는 사유를 실은 로그인 화면. */
function routeAfterSupersededSignal(signedOutBeforehand: boolean): string {
  if (signedOutBeforehand) return SIGNED_OUT_ROUTE;
  return isPartnerSsoSession() ? SUPERSEDED_PARTNER_LOGIN_ROUTE : SUPERSEDED_LOGIN_ROUTE;
}

/**
 * 처리 순서:
 * 1. fetch 관문을 밀려남 모드로 바꾼다(이후 main-api 요청은 네트워크 없이 실패, 진행 중 SSE 는 끊김).
 * 2. 오류 토스트를 억제하고(`notice("error", …)` 로 띄우는 토스트만 — 직접 부른 `toast.error` 는 막지 못한다)
 *    떠 있는 토스트를 닫는다.
 * 3. `broadcast` 면 다른 탭에 `superseded` 를 전파한다(전파받은 탭은 재전파하지 않는다). 가장 최근 탭 사건이
 *    로그아웃이면 `broadcast` 와 무관하게 전파하지 않는다.
 * 4. 이 브라우저에서만 로그아웃한다(5초 상한, 넘기면 토큰 직접 정리). 토큰이 남은 채 이동하면 가드가 로그인
 *    화면을 건너뛰고 앱으로 넘겨 보내 밀려남 안내가 보이지 않는다.
 * 5. 쿼리 캐시를 비운다.
 * 6. 종착지로 소프트 내비게이션한다. 가장 최근 탭 사건이 로그아웃이면 사유 없는 홈(`/`), 아니면 사유를 실은
 *    로그인 화면이다(파트너 SSO 세션이면 `superseded-partner`).
 * 7. 관문과 토스트를 되돌린다. 새 로그인의 점유가 막히지 않게 하기 위해서다.
 *
 * 1–4 는 앱 의존 없이 바로 진행하고, 5–6 은 의존이 주입될 때까지 기다린다. 1–2 는 동기로 끝나므로 감지 지점이
 * 응답을 넘기기 전에 이미 억제가 켜져 있다. 언제나 resolve 한다.
 *
 * 가장 최근 탭 사건이 로그아웃(`signed-out`)이면, 그 뒤의 밀려남 신호는 해제된 로그인이 받은 응답이다 — 해제 직후
 * 다른 탭의 상태 확인이 그렇다. 그때는 밀려남이 아니라 로그아웃으로 마친다: 사유 없이 홈으로 옮기고 `superseded`
 * 를 전파하지 않는다(엉뚱한 "다른 기기에서 로그인" 안내와 로그아웃 중인 탭으로의 역전파를 막는다).
 */
async function runLoginSupersededHandling(broadcast: boolean): Promise<void> {
  const signedOutBeforehand = readLoginTabSignal()?.type === "signed-out";
  // 갈 곳은 로그아웃 전에 고른다(출처 기록은 Cognito 키와 별개라 로그아웃 뒤에도 남지만 판정 시점을 앞에 둔다)
  const destination = routeAfterSupersededSignal(signedOutBeforehand);
  enterLoginSupersededFetchMode();
  muteErrorNotices();
  dismissAllNotices();
  try {
    if (broadcast && !signedOutBeforehand) writeLoginTabSignal("superseded");
    await signOutThisBrowser();
    const deps = await waitForDeps();
    deps.clearQueryCache();
    await deps.replaceRoute(destination);
  } catch {
    // 내비게이션 실패 — 토큰은 이미 지워졌고, 다음 감지가 다시 처리한다
  } finally {
    exitLoginSupersededFetchMode();
    unmuteErrorNotices();
  }
}

/**
 * 밀려남을 처리한다. 진행 중이면 새로 시작하지 않고 진행 중인 처리를 돌려준다.
 *
 * 가장 최근 탭 사건이 로그아웃(`signed-out`)이면 밀려남이 아니라 로그아웃으로 마친다 — `broadcast` 와 무관하게
 * `superseded` 를 전파하지 않고, 사유를 실은 로그인 화면 대신 사유 없는 홈(`/`)으로 옮긴다.
 */
export function handleLoginSuperseded({
  broadcast,
}: {
  /** 다른 탭에 `superseded` 를 전파할지. 가장 최근 탭 사건이 로그아웃이면 `true` 여도 전파하지 않는다. */
  broadcast: boolean;
}): Promise<void> {
  if (!inProgress) {
    inProgress = runLoginSupersededHandling(broadcast).finally(() => {
      inProgress = null;
    });
  }
  return inProgress;
}

export function isLoginSupersededHandling(): boolean {
  return inProgress !== null;
}
